import * as React from "react";
import { I18n } from "react-redux-i18n";
import { Modal, ModalBody, ModalHeader, ModalTitle } from '@common/components/modal';

import { getTestAttributes } from "@common/utils/tests";
import { formatDate } from "@common/utils/date";
import { IndividualListDto } from "@common/dto/individual/list-dto";

import { HttpErrorDto } from "@common/dto/http";
import { InviteHistoryDto } from "@common/dto/invite-history/list-dto";
import { InviteListDto } from "@common/dto/invite/list-dto";
import { MODULE_NAME } from "@modules/individuals";
import { generateId } from "@common/utils/common";
import { NewTable } from "@common/components/new-table";

import "./view-invite-history.styles.scss";

type Row = {};
type Cell = {};

type InternalInviteList = InviteHistoryDto & { id: number | string };

export type ViewInviteHistoryProps = {
  onClose: () => void;
  individual: { id: IndividualListDto['id']; name: string };
  invite: InviteListDto;
  showErrorMessage: (message: string) => void;
  getInviteHistory: ({ inviteId, globalId, fhq4 }: { inviteId: InviteListDto['id'], globalId: InviteListDto['globalId'], fhq4: InviteListDto['fhq4'] }) => Promise<InviteHistoryDto[]>;
};

export const ViewInviteHistory: React.FC<ViewInviteHistoryProps> = (props) => {
  const { individual, invite, showErrorMessage, getInviteHistory } = props;

  const [inviteHistories, setInviteHistories] = React.useState<InternalInviteList[]>(
    []
  );

  const modalTitle = React.useMemo(
    () => `${I18n.t("View Invite History for")} ${individual.name} ${I18n.t("to")} ${invite.name}`,
    [individual.name, invite.name]
  );

  const columns = React.useMemo(() => [
    { header: I18n.t('Date'), accessorKey: 'logEntryDate',
      cell: ({ getValue }: { getValue: () => any }) => {
        const dateValue = getValue();
        if (!dateValue) return '';
        return formatDate(dateValue, { format: "YYYY-MM-DD HH:mm" });
      } },
    { header: I18n.t('Status'), accessorKey: 'historyText' },
  ].map(column => ({
    ...column,
    id: column.accessorKey,
    disableActions: true,
    enableResizing: false,
    enableSorting: false,
  })), []);

  const handleInviteHistory = React.useCallback(async () => {
    try {
      const histories: InviteHistoryDto[] = await getInviteHistory({
        globalId: individual.id!,
        inviteId: invite.id,
        fhq4: invite.fhq4
      });
      setInviteHistories(histories.map(history => ({ ...history, id: generateId() })));

    } catch(err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto)?.entity || I18n.t('Failed to update status'));
    }
  }, [individual.id, invite.id, invite.fhq4, showErrorMessage, getInviteHistory]);

  const getId = React.useMemo(() => (
    {
      header: () => getTestAttributes({ id: "hp-invites-history-wnd-panel-invite-hist-grid-thead-row0" }).id,
      headerCell: (cell: Cell, index: number) => getTestAttributes({ id: `hp-invites-history-wnd-panel-invite-hist-grid-thead-row0-cell${index}` }).id,
      bodyRow: (row: Row, index: number) => getTestAttributes({ id: `hp-invites-history-wnd-panel-invite-hist-grid-tbody-row${index}` }).id,
      bodyCell: (row: Row, rowIndex: number, cell: Cell, cellIndex: number) => getTestAttributes({ id: `hp-invites-history-wnd-panel-invite-hist-grid-tbody-row${rowIndex}-cell${cellIndex}` }).id,
    }
  ), []);

  return (
    <Modal
      show
      bsSize="medium"
      bsClass="ViewInviteHistory bootstrap modal"
      onHide={props.onClose}
      onEscapeKeyDown={props.onClose}
      animation={false}
    >
      <ModalHeader closeButton onHide={props.onClose}>
        <ModalTitle
          {...getTestAttributes({ id: "lbl-invites-history-wnd-header" })}
        >
          {modalTitle}
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        <div className="ViewInviteHistory__content">
          <NewTable
            onLoadData={handleInviteHistory}
            data={inviteHistories}
            getId={getId}
            itemUniqProperty="id"
            moduleName={MODULE_NAME}
            columns={columns}
            noDataMessage={I18n.t("There no invite histories to display")}
            errorMessage={{ list: I18n.t('Failed to load invite history') }}
          />
        </div>
      </ModalBody>
    </Modal>
  );
};
